/**
 * ReplyComposer — multi-line text composer with collapse-on-blur behavior.
 *
 * Matches the Figma "Input Fields / Type=Free text" component set
 * (fileKey QkVMIm6TUdblppfqYlUtai, nodes 1135:6452 / 1135:6590 light,
 * 1135:6550 / 1135:6709 dark).
 *
 * Two visible states:
 *
 *   Collapsed (idle)  — Not focused AND value is empty. Renders at minimum
 *                       height (~36px, single-line look). Only the textarea
 *                       is visible; the bottom-row buttons are hidden. No
 *                       focus ring.
 *
 *   Expanded (active) — Focused OR value is non-empty. Height grows to fit
 *                       content up to `maxHeight` (default 120px), then
 *                       scrolls. Bottom row appears: emoji button (left, only
 *                       when `onEmojiClick` is provided) + send button (right).
 *                       Border switches to --stroke-input-active with a 2px
 *                       violet shadow ring. Dark theme also swaps background
 *                       to --bg-secondary.
 *
 * Behavior highlights:
 *   - Enter (no Shift) → onSend(), guarded on empty value.
 *   - Shift+Enter → newline (textarea default).
 *   - Auto-resize on input (height = auto → scrollHeight, clamped at maxHeight).
 *   - Height transition (150ms ease) only on collapse/expand, not every keystroke.
 *   - forwardRef<HTMLTextAreaElement> so consumers can imperatively focus.
 *   - Emoji button rendered ONLY when onEmojiClick is provided — no dead UI.
 *   - Send button disabled when: value empty, sending, or disabled.
 *   - send button shows Spinner when sending === true.
 *
 * Theme: [data-theme="dark"] parent selector — NOT a prop.
 *
 * Token references:
 *   - border default        → --stroke-neutral
 *   - border active         → --stroke-input-active
 *   - focus ring            → color-mix(--stroke-input-active, 20%) (derived from the active-stroke token)
 *   - bg default            → --bg-primary
 *   - bg dark active        → --bg-secondary
 *   - placeholder           → --text-placeholder
 *   - border-radius         → --radius-10
 *   - icon button radius    → --radius-4
 */

import { FaceSmile, Send03 } from "@untitledui/icons";
import {
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Spinner } from "../Spinner";
import styles from "./ReplyComposer.module.css";

export interface ReplyComposerProps {
  /**
   * Required accessible label. Hidden by default (hideLabel=true) — the reply
   * context in the surrounding notification card already carries meaning for
   * sighted users.
   */
  label: string;
  /**
   * When true, label is visually hidden but still announced by screen readers.
   * Default: true.
   */
  hideLabel?: boolean;
  /** Placeholder shown in the collapsed/idle state. Default: "Reply". */
  placeholder?: string;
  /** Controlled value. */
  value: string;
  /** Change handler — called with the new string on every input event. */
  onChange: (value: string) => void;
  /**
   * Send handler — called when the user presses Enter (no Shift) or clicks
   * send. Guard: does NOT fire when value.trim() === "".
   */
  onSend: () => void;
  /** When true, input and buttons are disabled (no interaction). */
  disabled?: boolean;
  /**
   * When true, the send button shows a spinner and is disabled. Input remains
   * enabled so consumers can decide whether to freeze typing during send.
   */
  sending?: boolean;
  /**
   * Optional emoji-button click handler. When OMITTED, the emoji button is
   * NOT rendered at all (no dead UI). When provided, consumer owns the emoji
   * picker implementation.
   */
  onEmojiClick?: () => void;
  /**
   * Maximum height in px of the expanded textarea before scrolling.
   * Default: 120.
   */
  maxHeight?: number;
  /** ARIA label for the send icon button. Default: "Send". */
  sendLabel?: string;
  /** ARIA label for the emoji icon button. Default: "Add emoji". */
  emojiLabel?: string;
  /** Optional className passthrough. */
  className?: string;
}

export const ReplyComposer = forwardRef<HTMLTextAreaElement, ReplyComposerProps>(
  function ReplyComposer(
    {
      label,
      hideLabel = true,
      placeholder = "Reply",
      value,
      onChange,
      onSend,
      disabled = false,
      sending = false,
      onEmojiClick,
      maxHeight = 120,
      sendLabel = "Send",
      emojiLabel = "Add emoji",
      className,
    },
    ref
  ) {
    const autoId = useId();
    const textareaId = autoId;

    // Track focus state so we can control expanded/collapsed.
    const [isFocused, setIsFocused] = useState(false);

    // Internal ref to the textarea for height management.
    // We need both the forwarded ref and an internal one; merge via callback ref.
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    // Merge forwarded ref + internal ref.
    const setRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    // Component is "expanded" when focused OR has content.
    const isExpanded = isFocused || value.trim() !== "";

    // Resize the textarea to fit content, capped at maxHeight.
    const resizeTextarea = useCallback(
      (el: HTMLTextAreaElement) => {
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
      },
      [maxHeight]
    );

    // Re-sync textarea height whenever `value` changes externally (e.g. when a
    // consumer resets value to "" after a successful send). Without this, the
    // textarea retains its inflated style.height after the content is cleared.
    // `value` isn't read inside the effect — it's the intentional trigger for
    // re-running the resize. Removing it breaks the fix.
    // biome-ignore lint/correctness/useExhaustiveDependencies: value is the re-sync trigger
    useEffect(() => {
      if (internalRef.current) resizeTextarea(internalRef.current);
    }, [value, resizeTextarea]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
        resizeTextarea(e.target);
      },
      [onChange, resizeTextarea]
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (value.trim() !== "" && !sending && !disabled) {
            onSend();
          }
        }
      },
      [value, onSend, sending, disabled]
    );

    const handleSendClick = useCallback(() => {
      if (value.trim() !== "" && !sending && !disabled) {
        onSend();
      }
    }, [value, onSend, sending, disabled]);

    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback(() => setIsFocused(false), []);

    const isSendDisabled = value.trim() === "" || sending || disabled;

    return (
      <div
        className={[
          styles.composer,
          isExpanded ? styles.expanded : styles.collapsed,
          disabled ? styles.disabled : null,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <label
          htmlFor={textareaId}
          className={hideLabel ? styles.labelVisuallyHidden : styles.label}
        >
          {label}
        </label>

        <div className={styles.inner}>
          <textarea
            ref={setRef}
            id={textareaId}
            className={styles.textarea}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            style={{ maxHeight: `${maxHeight}px` }}
            aria-label={hideLabel ? label : undefined}
          />

          {/* Bottom row — only visible in expanded state. Visibility is
           *  purely CSS-driven (`display: none` in collapsed, `display: flex`
           *  in expanded); no `aria-hidden` needed because `display:none`
           *  already removes the element from the accessibility tree. */}
          <div className={styles.bottomRow}>
            {onEmojiClick != null ? (
              <button
                type="button"
                className={styles.iconBtn}
                onClick={onEmojiClick}
                disabled={disabled}
                aria-label={emojiLabel}
                tabIndex={isExpanded ? 0 : -1}
              >
                <FaceSmile size={16} aria-hidden="true" />
              </button>
            ) : (
              // Spacer to keep send button pinned to the right when emoji is absent.
              <span className={styles.spacer} />
            )}

            <button
              type="button"
              className={[styles.iconBtn, styles.sendBtn].join(" ")}
              onClick={handleSendClick}
              disabled={isSendDisabled}
              aria-label={sendLabel}
              tabIndex={isExpanded ? 0 : -1}
            >
              {sending ? (
                <Spinner size="xs" tone="inherit" label={sendLabel} />
              ) : (
                <Send03 size={16} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
);
